import { useCallback, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { UploadCloud, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImageDropzone({ value, onChange, testid = "image-dropzone" }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const previewSrc = value
    ? value.startsWith("/api/") || value.startsWith("api/")
      ? `${BACKEND_URL}${value.startsWith("/") ? "" : "/"}${value}`
      : value
    : "";

  const handleFiles = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;
      const file = files[0];
      if (!file.type.startsWith("image/")) {
        toast.error("Please choose an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be under 5MB");
        return;
      }
      setUploading(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/admin/uploads", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onChange(data.url);
        toast.success("Image uploaded");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setUploading(false);
      }
    },
    [onChange]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={
          "relative flex items-center gap-4 border-2 border-dashed rounded-md p-4 cursor-pointer transition-colors " +
          (dragOver
            ? "border-[#1e392a] bg-[#efeadd]"
            : "border-[#e4e4e7] bg-white hover:border-[#1e392a]")
        }
        data-testid={`${testid}-drop`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`${testid}-input`}
          onChange={(e) => handleFiles(e.target.files)}
        />

        <div className="w-20 h-20 flex-shrink-0 rounded bg-[#efeadd] overflow-hidden flex items-center justify-center">
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-[#8a8d86]" />
          ) : previewSrc ? (
            <img src={previewSrc} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-[#8a8d86]" strokeWidth={1.5} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm text-[#1e392a]">
            <UploadCloud className="h-4 w-4" strokeWidth={1.5} />
            <span className="font-medium">
              {uploading ? "Uploading…" : "Drop image or click to upload"}
            </span>
          </div>
          <p className="text-xs text-[#8a8d86] mt-1 truncate">
            JPG, PNG, WEBP up to 5MB{value ? ` · ${value}` : ""}
          </p>
        </div>

        {value && !uploading && (
          <button
            type="button"
            data-testid={`${testid}-remove`}
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            className="p-2 rounded-full hover:bg-[#f4f4f5]"
            aria-label="Remove image"
          >
            <X className="h-4 w-4 text-[#71717a]" />
          </button>
        )}
      </div>
    </div>
  );
}
